import { useEffect, useMemo } from 'react';
import { useEditor, EditorContent, ReactNodeViewRenderer, NodeViewWrapper } from '@tiptap/react';
import type { NodeViewProps } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Image from '@tiptap/extension-image';
import Link from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import { Node, mergeAttributes } from '@tiptap/core';
import {
  Bold,
  Italic,
  List,
  ListOrdered,
  Quote,
  Heading2,
  Heading3,
  Link2,
  Minus,
  Code2,
  Music,
  Film,
  ImageIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import type { TiptapDoc } from '@/types/content';

// SPEC-021: Tiptap editor wrapper. The body is stored as a JSONB Tiptap
// doc so no HTML ever round-trips. Two custom nodes — audioBlock and
// videoEmbed — are rendered inline with placeholder cards; the mini app
// takes the same JSONB tree and swaps in real <audio>/iframe players.

// ---------------------------------------------------------------------------
// Custom node: audioBlock
// ---------------------------------------------------------------------------

interface AudioBlockAttrs {
  assetId?: string;
  url?: string;
  title?: string;
}

function AudioBlockView({ node, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as AudioBlockAttrs;
  return (
    <NodeViewWrapper className="my-3">
      <div className="flex items-center gap-3 rounded-md border border-dashed bg-muted/40 p-3">
        <Music className="h-5 w-5 text-muted-foreground" />
        <div className="flex-1 text-sm">
          <p className="font-medium">{attrs.title ?? 'Audio block'}</p>
          <p className="text-xs text-muted-foreground">
            {attrs.assetId ? `asset ${attrs.assetId.slice(0, 8)}…` : 'unbound asset'}
          </p>
        </div>
        <Button size="sm" variant="ghost" onClick={() => deleteNode()}>
          Remove
        </Button>
      </div>
    </NodeViewWrapper>
  );
}

const AudioBlock = Node.create({
  name: 'audioBlock',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      assetId: { default: null },
      url: { default: null },
      title: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="audio-block"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'audio-block' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(AudioBlockView);
  },
});

// ---------------------------------------------------------------------------
// Custom node: videoEmbed
// ---------------------------------------------------------------------------

interface VideoEmbedAttrs {
  provider?: string;
  videoId?: string;
  url?: string;
}

function VideoEmbedView({ node, deleteNode }: NodeViewProps) {
  const attrs = node.attrs as VideoEmbedAttrs;
  const thumb =
    attrs.provider === 'youtube' && attrs.videoId
      ? `https://img.youtube.com/vi/${attrs.videoId}/hqdefault.jpg`
      : null;
  return (
    <NodeViewWrapper className="my-3">
      <div className="overflow-hidden rounded-md border bg-muted/40">
        {thumb ? (
          <img src={thumb} alt="video preview" className="h-40 w-full object-cover" />
        ) : (
          <div className="flex h-40 items-center justify-center text-muted-foreground">
            <Film className="h-8 w-8" />
          </div>
        )}
        <div className="flex items-center justify-between px-3 py-2 text-sm">
          <div>
            <p className="font-medium capitalize">{attrs.provider ?? 'video'} embed</p>
            <p className="text-xs text-muted-foreground">{attrs.url}</p>
          </div>
          <Button size="sm" variant="ghost" onClick={() => deleteNode()}>
            Remove
          </Button>
        </div>
      </div>
    </NodeViewWrapper>
  );
}

const VideoEmbed = Node.create({
  name: 'videoEmbed',
  group: 'block',
  atom: true,
  selectable: true,
  addAttributes() {
    return {
      provider: { default: null },
      videoId: { default: null },
      url: { default: null },
    };
  },
  parseHTML() {
    return [{ tag: 'div[data-type="video-embed"]' }];
  },
  renderHTML({ HTMLAttributes }) {
    return ['div', mergeAttributes(HTMLAttributes, { 'data-type': 'video-embed' })];
  },
  addNodeView() {
    return ReactNodeViewRenderer(VideoEmbedView);
  },
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export interface TiptapEditorProps {
  value: TiptapDoc;
  onChange: (doc: TiptapDoc) => void;
  placeholder?: string;
  editable?: boolean;
}

export function TiptapEditor({
  value,
  onChange,
  placeholder = 'Start writing your content...',
  editable = true,
}: TiptapEditorProps) {
  const extensions = useMemo(
    () => [
      StarterKit.configure({
        heading: { levels: [2, 3] },
      }),
      Image,
      Link.configure({
        openOnClick: false,
        autolink: true,
        defaultProtocol: 'https',
      }),
      Placeholder.configure({ placeholder }),
      AudioBlock,
      VideoEmbed,
    ],
    [placeholder],
  );

  const editor = useEditor({
    extensions,
    content: value,
    editable,
    onUpdate: ({ editor: ed }) => {
      const json = ed.getJSON() as unknown as TiptapDoc;
      onChange(json);
    },
    editorProps: {
      attributes: {
        class:
          'prose prose-sm dark:prose-invert max-w-none min-h-[320px] focus:outline-none px-3 py-3',
      },
    },
  });

  // If the parent replaces `value` wholesale (e.g. after loading an item
  // by id), sync the editor state. Skip when the editor already matches to
  // avoid cursor jumps during normal typing.
  useEffect(() => {
    if (!editor) return;
    const current = JSON.stringify(editor.getJSON());
    const incoming = JSON.stringify(value);
    if (current !== incoming) {
      editor.commands.setContent(value as unknown as object, { emitUpdate: false });
    }
  }, [editor, value]);

  if (!editor) {
    return (
      <div className="rounded-md border bg-background p-4 text-sm text-muted-foreground">
        Loading editor…
      </div>
    );
  }

  return (
    <div className="rounded-md border bg-background">
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Toolbar
// ---------------------------------------------------------------------------

function Toolbar({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) {
  const btn = (active: boolean) =>
    cn(
      'h-8 w-8 rounded-sm p-0',
      active ? 'bg-accent text-accent-foreground' : 'hover:bg-muted',
    );

  const addLink = () => {
    const prev = editor.getAttributes('link').href as string | undefined;
    const url = window.prompt('Link URL', prev ?? 'https://');
    if (url === null) return;
    if (url === '') {
      editor.chain().focus().unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
  };

  const addImageByURL = () => {
    const url = window.prompt('Image URL');
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  };

  return (
    <div className="flex flex-wrap items-center gap-1 border-b px-2 py-1">
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('heading', { level: 2 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        title="Heading 2"
      >
        <Heading2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('heading', { level: 3 }))}
        onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        title="Heading 3"
      >
        <Heading3 className="h-4 w-4" />
      </Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('bold'))}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold"
      >
        <Bold className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('italic'))}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic"
      >
        <Italic className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('code'))}
        onClick={() => editor.chain().focus().toggleCode().run()}
        title="Inline code"
      >
        <Code2 className="h-4 w-4" />
      </Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('bulletList'))}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet list"
      >
        <List className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('orderedList'))}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list"
      >
        <ListOrdered className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('blockquote'))}
        onClick={() => editor.chain().focus().toggleBlockquote().run()}
        title="Quote"
      >
        <Quote className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(false)}
        onClick={() => editor.chain().focus().setHorizontalRule().run()}
        title="Divider"
      >
        <Minus className="h-4 w-4" />
      </Button>
      <span className="mx-1 h-5 w-px bg-border" />
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(editor.isActive('link'))}
        onClick={addLink}
        title="Link"
      >
        <Link2 className="h-4 w-4" />
      </Button>
      <Button
        type="button"
        size="icon"
        variant="ghost"
        className={btn(false)}
        onClick={addImageByURL}
        title="Image by URL"
      >
        <ImageIcon className="h-4 w-4" />
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Helpers exported for pages that insert custom blocks imperatively
// (MediaUploader insertion path, VideoEmbedInput insertion path).
// ---------------------------------------------------------------------------

export function insertAudioBlock(
  editor: NonNullable<ReturnType<typeof useEditor>>,
  attrs: AudioBlockAttrs,
) {
  editor.chain().focus().insertContent({ type: 'audioBlock', attrs }).run();
}

export function insertVideoEmbed(
  editor: NonNullable<ReturnType<typeof useEditor>>,
  attrs: VideoEmbedAttrs,
) {
  editor.chain().focus().insertContent({ type: 'videoEmbed', attrs }).run();
}
